import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type { EnvironmentClassification, EnvironmentKind } from '@verydia/ui';
import { useMe } from './api.ts';
import { useEnvironments, useProducts } from './identity-queries.ts';
import { useAuth } from './auth/index.ts';
import {
  EMPTY_CONTEXT,
  catalogContextReducer,
  type CatalogContextState,
} from './catalog-context.ts';

/**
 * Organization / product / environment context (§31).
 *
 * From VC-1 the organization list is real: it comes from `/v1/me`, which returns
 * exactly the organizations this person is a member of. That is what makes the
 * switcher safe to render — it cannot list an organization the API would refuse
 * to open, because the API is what produced the list.
 */
export interface OrganizationContext {
  id: string;
  name: string;
  slug: string;
  /** This user's role in that organization. Shown in the switcher. */
  note: string;
}

export interface ProductContext {
  id: string;
  name: string;
  slug: string;
}

export interface EnvironmentContext {
  id: string;
  name: string;
  slug: string;
  kind: EnvironmentKind;
  /** The load-bearing one — what the selector badges, and what accounting reads. */
  classification: EnvironmentClassification;
}

interface ConsoleContextValue {
  organizations: readonly OrganizationContext[];
  organization: OrganizationContext | null;
  selectOrganization: (id: string) => void;

  /** Selectable products: active ones. An archived product is not a working context. */
  products: readonly ProductContext[];
  /**
   * Whether the organization has any product at all, archived included.
   *
   * Distinct from `products.length` on purpose. "No products yet" and "none of
   * them are active" are different facts, and the first is a lie when an
   * archived product exists — the operator created one, it is on the list page,
   * and the console telling them otherwise is the placeholder-lie defect in a
   * new costume.
   */
  hasAnyProducts: boolean;
  product: ProductContext | null;
  selectProduct: (id: string) => void;

  environments: readonly EnvironmentContext[];
  environment: EnvironmentContext | null;
  selectEnvironment: (id: string) => void;
  /** Deliberate return to "no environment selected". */
  clearEnvironment: () => void;

  /**
   * Whether the child lists are still being fetched.
   *
   * Loading is not empty and empty is not unavailable. Without this the bar
   * cannot tell "this product has no environments" from "we have not asked
   * yet", and it would state the first while the second is true.
   */
  productsLoading: boolean;
  environmentsLoading: boolean;
}

/*
 * There are deliberately no seeded environments here.
 *
 * VC-0 hard-coded Development, Pilot and Production and selected Development, so
 * the context bar read "PRODUCT: No products yet / ENVIRONMENT: Development" —
 * a state the model does not permit. An environment belongs to a product; with
 * no product there is nothing for one to belong to.
 *
 * That is not a labelling problem. A fabricated environment id in context is a
 * value that future requests would carry into URLs and API calls, and the bug
 * would surface as a cross-product mix-up long after the display was fixed.
 *
 * Real environments arrive with the environment API in Checkpoint C. Until then
 * this list is empty, which is the truth.
 */

/** `ORGANIZATION_READ_ONLY` reads badly in a switcher; "Read only" does not. */
function humaniseRole(role: string): string {
  const withoutPrefix = role
    .replace(/^ORGANIZATION_/, '')
    .replaceAll('_', ' ')
    .toLowerCase();
  return withoutPrefix.charAt(0).toUpperCase() + withoutPrefix.slice(1);
}

const ConsoleContext = createContext<ConsoleContextValue | null>(null);

/** Survives a reload, so an operator returns to the organization they were in. */
const SELECTED_ORGANIZATION_KEY = 'vc.console.organization';

export function ConsoleContextProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const me = useMe(auth.status === 'authenticated');

  /*
    One reducer, not three setters.

    The transitions live in `catalog-context.ts` as a pure function so the
    invariant — an environment implies a product, a product implies an
    organization — is enforced structurally rather than repaired afterwards by
    effects. Exposing `setProductId` to callers is what allowed a product from
    one organization to sit under another for as long as it took a query to
    resolve.
  */
  const [state, dispatch] = useReducer(catalogContextReducer, EMPTY_CONTEXT, (empty) => {
    try {
      const remembered = localStorage.getItem(SELECTED_ORGANIZATION_KEY);
      // An untrusted preference, not a selection. It is validated against the
      // authorized membership list below before anything is fetched with it.
      return remembered === null ? empty : { ...empty, organizationId: remembered };
    } catch {
      return empty;
    }
  });
  const { organizationId, productId, environmentId } = state satisfies CatalogContextState;

  // The product selector reads the same catalog the Products page does, so it
  // cannot keep claiming "No products yet" once one exists.
  //
  // Gated on authentication, not just on having an organization id. The id is
  // restored from localStorage on the very first render, so without this the
  // query fired before a token existed and every page load began with a 401
  // that then silently succeeded on retry — invisible in the UI, obvious in the
  // network panel.
  const productsQuery = useProducts(auth.status === 'authenticated' ? organizationId : null);
  const allProducts = useMemo(() => productsQuery.data?.data.data ?? [], [productsQuery.data]);
  const catalog = useMemo(() => allProducts.filter((p) => p.status === 'ACTIVE'), [allProducts]);

  const organizations = useMemo<readonly OrganizationContext[]>(
    () =>
      (me.data?.data.organizations ?? []).map((membership) => ({
        id: membership.organization_id,
        name: membership.display_name,
        slug: membership.slug,
        note: humaniseRole(membership.role),
      })),
    [me.data],
  );

  // Falling back keeps a revoked membership from leaving the console with
  // nothing selected. It is a separate decision from reconciliation: that one
  // removes what is gone, this one chooses a replacement.
  useEffect(() => {
    if (organizationId !== null || organizations.length === 0) return;
    dispatch({ type: 'selectOrganization', organizationId: organizations[0]!.id });
  }, [organizationId, organizations]);

  useEffect(() => {
    if (organizationId === null) return;
    try {
      localStorage.setItem(SELECTED_ORGANIZATION_KEY, organizationId);
    } catch {
      /* a console that cannot remember the selection still works */
    }
  }, [organizationId]);

  /**
   * Environments of the SELECTED product, and only of it.
   *
   * Keyed on the product, so the query is not even issued without one — an
   * environment belongs to a product, and a list gathered across products would
   * offer a choice that cannot be valid. When the product clears, this clears
   * with it, without anyone having to remember to reset it.
   *
   * Archived environments are filtered out: they remain readable at their own
   * address, but they are not a working context to select.
   */
  const environmentsQuery = useEnvironments(
    auth.status === 'authenticated' ? organizationId : null,
    productId,
  );
  const environments = useMemo<readonly EnvironmentContext[]>(
    () =>
      productId === null
        ? []
        : (environmentsQuery.data?.data.data ?? [])
            .filter((environment) => environment.status === 'ACTIVE')
            .map((environment) => ({
              id: environment.id,
              name: environment.name,
              slug: environment.slug,
              kind: environment.kind,
              classification: environment.classification,
            })),
    [productId, environmentsQuery.data],
  );

  /*
    One reconciliation, against everything the server currently says.

    A remembered organization can name a membership that was revoked; a selected
    product can have been archived from another tab; a selected environment can
    have disappeared under it. All three are the same question — "does the server
    still agree this exists?" — so they are answered in one place, once, rather
    than by three effects that can each fire on a different render and briefly
    disagree with each other.

    `null` for a child list means "not loaded", which the reducer treats as "do
    not touch". Only a loaded list can retire a selection.
  */
  useEffect(() => {
    if (organizations.length === 0) return;
    dispatch({
      type: 'reconcile',
      organizationIds: organizations.map((organization) => organization.id),
      productIds: productsQuery.data === undefined ? null : catalog.map((product) => product.id),
      environmentIds: environmentsQuery.data === undefined ? null : environments.map((e) => e.id),
    });
  }, [organizations, productsQuery.data, catalog, environmentsQuery.data, environments]);

  /*
    Every selection carries the parent it believes it belongs to, so the reducer
    can refuse a mismatch outright. The parent is read from current state rather
    than from the caller: a caller that could name its own parent could name the
    wrong one, which is the mix-up this is here to prevent.
  */
  const selectOrganization = useCallback(
    (id: string) => dispatch({ type: 'selectOrganization', organizationId: id }),
    [],
  );
  const selectProduct = useCallback(
    (id: string) =>
      dispatch({ type: 'selectProduct', productId: id, organizationId: organizationId ?? '' }),
    [organizationId],
  );
  const selectEnvironment = useCallback(
    (id: string) =>
      dispatch({ type: 'selectEnvironment', environmentId: id, productId: productId ?? '' }),
    [productId],
  );
  const clearEnvironment = useCallback(() => dispatch({ type: 'clearEnvironment' }), []);

  const value = useMemo<ConsoleContextValue>(() => {
    const products: readonly ProductContext[] = catalog.map((product) => ({
      id: product.id,
      name: product.name,
      slug: product.slug,
    }));
    return {
      organizations,
      organization: organizations.find((o) => o.id === organizationId) ?? null,
      selectOrganization,

      products,
      hasAnyProducts: allProducts.length > 0,
      product: products.find((p) => p.id === productId) ?? null,
      selectProduct,

      environments,
      environment: environments.find((e) => e.id === environmentId) ?? null,
      selectEnvironment,
      clearEnvironment,

      // `isPending` alone would read as loading forever for a query that is
      // disabled, which is what an environment query with no product is.
      productsLoading:
        organizationId !== null && productsQuery.isPending && productsQuery.isFetching,
      environmentsLoading:
        productId !== null && environmentsQuery.isPending && environmentsQuery.isFetching,
    };
  }, [
    organizations,
    organizationId,
    allProducts,
    catalog,
    productId,
    environments,
    environmentId,
    selectOrganization,
    selectProduct,
    selectEnvironment,
    clearEnvironment,
    productsQuery.isPending,
    productsQuery.isFetching,
    environmentsQuery.isPending,
    environmentsQuery.isFetching,
  ]);

  return <ConsoleContext.Provider value={value}>{children}</ConsoleContext.Provider>;
}

export function useConsoleContext(): ConsoleContextValue {
  const value = useContext(ConsoleContext);
  if (value === null) {
    throw new Error('useConsoleContext must be used inside <ConsoleContextProvider>.');
  }
  return value;
}
